import React, { FC, ReactNode } from 'react'
import { useRouteMatch } from 'react-router'
import {
  ArtboardDetailInfoFragment,
  PrototypeDetailsInfoFragment,
  GetArtboardsForDocumentQuery,
  GetArtboardsForPageQuery,
} from 'gqlTypes'
import { ApolloError, ObservableQuery, OperationVariables } from 'apollo-client'
import get from 'lodash.get'
import groupBy from 'lodash.groupby'
import theme from 'styles/theme'
import { getArtboardThumbnail } from 'modules/shares/utils'
import { ResponsiveValues } from 'styles/breakpoints'
import Box from 'components/Box'
import Artboard from 'modules/shares/components/Artboard'
import LoadingPlaceholder from 'components/LoadingPlaceholder'
import InfiniteList, { loadMore } from 'components/InfiniteList'
import { useVersioning } from 'modules/versioning'
import ErrorMessage from 'components/ErrorMessage'
import { useContainerRefScroll } from 'utils/ContainerRefScrollContext'
import { createThumbnailSrcSetForFiles } from 'modules/shares/components/ArtboardView/utils'
import { ArtboardFile } from 'modules/shares/components/ArtboardView/ArtboardView'
import Tooltip from 'components/Tooltip'
import AspectRatioGrid, { ResponseProp } from 'components/AspectRatioGrid'
import PrototypeCard from 'components/PrototypeCard'
import { isDocumentRoute } from 'components/Breakpoint/utils'
import { useWebRendererSupport } from '../WebRenderer/utils'
import { useFlag } from 'constants/flags'
import { ReactComponent as CanvasTooltip } from 'icons/canvas-tooltip.svg'
import { ReactComponent as DocumentIcon } from 'icons/document-64.svg'
import { ReactComponent as MagGlassIcon } from 'icons/mag-glass-64.svg'

import {
  Container,
  FullScreenContainer,
  StyledBetaPill,
  StyledArrowRightCircleIcon,
  StyledPageCanvasLink,
  StyledPageLink,
  StyledPageTitleContainer,
  StyledVersionLink,
  Title,
} from './ArtboardList.styles'

const VERTICAL_ROW_SPACE_GRID = [28, 28, 32] as [number, number, number]

const defaultColumns: ResponseProp = [2, 2, 3, 4, 5, 6, 7]

const PageCanvasLinkTooltip: FC = ({ children }) => (
  <Tooltip
    content={
      <>
        <Box maxWidth={225}>
          View all Artboards as they appear in the original document, including
          elements outside Artboards.
        </Box>
        <Box marginTop={3}>
          <CanvasTooltip width={225} />
        </Box>
      </>
    }
    placement="bottom">
    {children}
  </Tooltip>
)

type ArtboardsProps = {
  title: string
  shareID: string
  columns?: ResponseProp
  pageUUID: string
  children?: ReactNode
}

export const Artboards = ({
  title,
  columns = defaultColumns,
  children,
  shareID,
  pageUUID,
}: ArtboardsProps) => {
  const { getPathname } = useVersioning()
  const isDocumentView = useIsDocumentView()

  const canvasSupport = useWebRendererSupport()
  const webCanvasMvpFF = useFlag('web-canvas-mvp')

  const pagePath = getPathname({
    routeKey: 'SHARE_PAGE_VIEW',
    routeParams: { shareID, pageUUID },
  })

  const canvasPath = getPathname({
    routeKey: 'SHARE_PAGE_CANVAS_VIEW',
    routeParams: { shareID, pageUUID },
  })

  const showCanvasLink = canvasSupport.supported && webCanvasMvpFF

  return (
    <Container>
      <StyledPageTitleContainer>
        <Title>
          <StyledPageLink
            to={pagePath}
            external={false}
            // Avoids adding entries to the browser history if user clicks
            // on the link while already being on the page view.
            replace={!isDocumentView}>
            {title}
            {isDocumentView && <StyledArrowRightCircleIcon />}
          </StyledPageLink>
        </Title>
        {showCanvasLink && (
          <PageCanvasLinkTooltip>
            <StyledPageCanvasLink to={canvasPath}>
              Canvas View
              <StyledBetaPill
                variant={{
                  kind: 'special',
                  backgroundTint: theme.colors.primaryTint[10],
                  textTint: theme.colors.primaryTint[100],
                }}>
                Beta
              </StyledBetaPill>
            </StyledPageCanvasLink>
          </PageCanvasLinkTooltip>
        )}
      </StyledPageTitleContainer>
      <AspectRatioGrid
        verticalRowSpace={VERTICAL_ROW_SPACE_GRID}
        columns={columns}
        gutterSize={32}>
        {children}
      </AspectRatioGrid>
    </Container>
  )
}

interface ArtboardsListProps {
  fetchMore: ObservableQuery<any, OperationVariables>['fetchMore']
  afterPath: string[]
  entriesPath: string[]
  data?: GetArtboardsForDocumentQuery | GetArtboardsForPageQuery
  loading: boolean
  error?: ApolloError
  filterValue: string
  columns: ResponsiveValues<number>
  pageUUID?: string
}

/**
 * Handles an infinite list of artboards fetched by either the
 * GetArtboardsForDocumentQuery or the GetArtboardsForPageQuery.
 */
export const ArtboardList = ({
  fetchMore,
  afterPath,
  entriesPath,
  data,
  loading,
  error,
  filterValue,
  columns,
  pageUUID,
}: ArtboardsListProps) => {
  const { saveScrollTop } = useContainerRefScroll()
  const { getPathname, setArtboardThumbnail } = useVersioning()

  if (loading) {
    return (
      <FullScreenContainer>
        <LoadingPlaceholder size="64px" />
      </FullScreenContainer>
    )
  }

  if (error || !data?.share) return <ErrorMessage.Generic />

  const { commentsEnabled = true, identifier: shareID } = data.share
  const entries = get(data, entriesPath, []) as ArtboardDetailInfoFragment[]

  const prototypes = get(
    data,
    ['share', 'version', 'document', 'prototypes', 'entries'],
    []
  ) as PrototypeDetailsInfoFragment[]

  if (entries.length + prototypes.length === 0) {
    if (filterValue) {
      return (
        <ErrorMessage
          title="We couldn’t find any Artboards with that name."
          icon={<MagGlassIcon />}
          iconSize="medium">
          Press the Escape key to return to your document.
        </ErrorMessage>
      )
    }

    return (
      <ErrorMessage
        title="Nothing to see here"
        icon={<DocumentIcon />}
        iconSize="medium">
        This document has no content
      </ErrorMessage>
    )
  }

  const artboardsByPage = groupBy(entries, e => e.page?.identifier)
  const pages = Object.values(artboardsByPage)

  const handleArtboardClick = (artboard: ArtboardDetailInfoFragment) => {
    saveScrollTop()
    setArtboardThumbnail(getArtboardThumbnail(artboard))
  }

  return (
    <InfiniteList
      onLoadMore={loadMore(fetchMore, get(data, afterPath), entriesPath)}>
      {/* If there are prototypes in the data payload we should show them */}
      {prototypes.length > 0 && (
        <Container>
          <StyledPageTitleContainer>
            <Title>Prototypes</Title>
          </StyledPageTitleContainer>
          <AspectRatioGrid
            columns={columns}
            ratio={4 / 3}
            gutterSize={[16, 32]}
            verticalRowSpace={[16, 32]}>
            {prototypes.map(prototype => (
              <PrototypeCard
                pathname={getPathname({
                  routeKey: 'PROTOTYPE',
                  routeParams: {
                    shareID,
                    permanentArtboardShortId: prototype.permanentArtboardShortId!,
                  },
                })}
                key={prototype.identifier}
                title={prototype.name}
                src={getArtboardThumbnail(prototype)}
              />
            ))}
          </AspectRatioGrid>
        </Container>
      )}

      {pages.map(artboards => (
        <Artboards
          key={artboards[0]?.page?.uuid!}
          columns={columns}
          title={artboards[0]?.page?.name!}
          shareID={shareID}
          pageUUID={artboards[0]?.page?.uuid!}>
          {artboards.map(artboard => (
            <StyledVersionLink
              data-testid="artboard-link"
              onClick={() => handleArtboardClick(artboard)}
              key={artboard.identifier}
              to={{
                routeKey: 'ARTBOARD_DETAIL',
                routeParams: {
                  shareID,
                  permanentArtboardShortId: artboard.permanentArtboardShortId!,
                },
                state: {
                  fromOverview: true,
                  pageUUID,
                },
              }}>
              <Artboard
                title={artboard.name}
                imageSrc={getArtboardThumbnail(artboard)}
                srcSet={
                  createThumbnailSrcSetForFiles(
                    artboard?.files as ArtboardFile[],
                    'L'
                  ) ?? undefined
                }
                commentCount={
                  commentsEnabled ? artboard.artboardBase.commentCount : 0
                }
                hasNewComments={artboard.artboardBase.unreadCount! > 0}
                hightLightTitle={filterValue}
                width={artboard.width!}
              />
            </StyledVersionLink>
          ))}
        </Artboards>
      ))}
    </InfiniteList>
  )
}

/**
 * SHARE_VIEW or SHARE_PAGE_VIEW routes share the same view but
 * we need to sometimes differentiate them.
 */
function useIsDocumentView() {
  const { path } = useRouteMatch()
  const isDocumentView = isDocumentRoute(path)

  return isDocumentView
}
